import { jsPDF } from 'jspdf';

export interface GenerateProposalPdfOptions {
  title: string;
  agency: string;
  solicitationId: string;
  vendorName: string;
  vendorAddress: string;
  vendorEmail: string;
  dueDate: string;
  state: string;
}

export function generateProposalPdf(markdownText: string, options: GenerateProposalPdfOptions) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let y = 30;

  // Header and Footer helpers
  const drawHeaderFooter = (pageNum: number, totalPages: number) => {
    // Suppress header/footer on cover page (Page 1)
    if (pageNum === 1) return;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(150);

    // Header
    pdf.text(`SOLICITATION RESPONSE: ${options.solicitationId}`, margin, 12);
    pdf.text(options.agency.toUpperCase(), pageWidth - margin - pdf.getTextWidth(options.agency.toUpperCase()), 12);
    pdf.setDrawColor(220, 225, 230);
    pdf.setLineWidth(0.2);
    pdf.line(margin, 14, pageWidth - margin, 14);

    // Footer
    pdf.text(`Prepared By: ${options.vendorName}`, margin, pageHeight - 12);
    const pageText = `Page ${pageNum} of ${totalPages}`;
    pdf.text(pageText, pageWidth - margin - pdf.getTextWidth(pageText), pageHeight - 12);
  };

  // Check overflow and add page if needed
  const checkPageOverflow = (neededHeight: number) => {
    if (y + neededHeight > pageHeight - margin - 15) {
      pdf.addPage();
      y = margin + 10;
    }
  };

  // =========================================================================
  // COVER PAGE (Page 1)
  // =========================================================================
  // Draw deep luxury header stripe
  pdf.setFillColor(30, 27, 75); // Deep Indigo (#1e1b4b)
  pdf.rect(0, 0, pageWidth, 55, 'F');

  // Cover Page Title inside indigo stripe
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(255, 255, 255);
  const titleLines = pdf.splitTextToSize(options.title, contentWidth);
  pdf.text(titleLines, margin, 25);

  // Subtitle / Label
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(199, 210, 254); // Light lavender
  pdf.text(`OFFICIAL RESPONSE PROPOSAL TO STATE OF ${options.state.toUpperCase()}`, margin, 45);

  // Cover Page Body Content
  y = 75;

  // Metadata Card Background Block
  pdf.setFillColor(248, 250, 252); // Soft light blue-gray slate-50
  pdf.rect(margin, y, contentWidth, 75, 'F');
  pdf.setDrawColor(226, 232, 240); // Slate-200
  pdf.setLineWidth(0.4);
  pdf.rect(margin, y, contentWidth, 75, 'S');

  // Metadata block content
  pdf.setTextColor(71, 85, 105); // slate-600
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('SOLICITATION DETAILS', margin + 8, y + 10);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(51, 65, 85); // slate-700

  let innerY = y + 20;
  const drawMetadataLine = (label: string, val: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${label}:`, margin + 8, innerY);
    pdf.setFont('helvetica', 'normal');
    pdf.text(val, margin + 45, innerY);
    innerY += 8;
  };

  drawMetadataLine('Solicitation ID', options.solicitationId);
  drawMetadataLine('Target Agency', options.agency);
  drawMetadataLine('Jurisdiction State', options.state);
  drawMetadataLine('Response Deadline', options.dueDate ? new Date(options.dueDate).toLocaleDateString() : 'N/A');

  y += 90;

  // Vendor Information block
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(30, 27, 75);
  pdf.text('SUBMITTING VENDOR PROFILE', margin, y);
  y += 8;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(51, 65, 85);
  pdf.text(`Company Name: ${options.vendorName}`, margin, y);
  y += 6;
  pdf.text(`Mailing Address: ${options.vendorAddress || 'N/A'}`, margin, y);
  y += 6;
  pdf.text(`Primary Contact Email: ${options.vendorEmail || 'N/A'}`, margin, y);

  y += 25;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  pdf.text('CONFIDENTIAL & PROPRIETARY — Prepared exclusively for government evaluation purposes.', margin, y);

  // =========================================================================
  // BODY PAGES (Page 2+)
  // =========================================================================
  pdf.addPage();
  y = margin + 10;

  // Simple Markdown Parsing & Rendering Loop
  const lines = markdownText.split('\n');
  let inTable = false;
  let tableRows: string[][] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check for empty line
    if (!line) {
      if (inTable) {
        // Render completed table
        drawTable(pdf, tableRows, contentWidth, margin);
        tableRows = [];
        inTable = false;
      }
      y += 4;
      continue;
    }

    // Handle table rows
    if (line.startsWith('|')) {
      inTable = true;
      // Parse table cells
      const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      // Skip the separator line (e.g. |---|---|)
      if (!cells.every(c => c.startsWith('-'))) {
        tableRows.push(cells);
      }
      continue;
    } else {
      if (inTable) {
        drawTable(pdf, tableRows, contentWidth, margin);
        tableRows = [];
        inTable = false;
      }
    }

    // Handle Headings
    if (line.startsWith('# ')) {
      const headingText = line.substring(2);
      checkPageOverflow(18);
      y += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.setTextColor(30, 27, 75); // Indigo-950
      pdf.text(headingText, margin, y);
      y += 8;
    } else if (line.startsWith('## ')) {
      const headingText = line.substring(3);
      checkPageOverflow(15);
      y += 4;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(79, 70, 229); // Indigo-600
      pdf.text(headingText, margin, y);
      y += 6;
    } else if (line.startsWith('### ')) {
      const headingText = line.substring(4);
      checkPageOverflow(12);
      y += 3;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(17, 24, 39); // Gray-900
      pdf.text(headingText, margin, y);
      y += 5;
    } 
    // Handle Bullet points
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      const bulletText = line.substring(2).replace(/\*\*/g, ''); // strip bold stars
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9.5);
      pdf.setTextColor(51, 65, 85);
      
      const splitLines = pdf.splitTextToSize(bulletText, contentWidth - 6);
      checkPageOverflow(splitLines.length * 4.5 + 2);
      
      // Draw actual bullet symbol
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(79, 70, 229);
      pdf.text('•', margin + 2, y + 1);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      pdf.text(splitLines, margin + 6, y + 1);
      y += (splitLines.length * 4.5) + 2;
    } 
    // Normal Paragraph Text
    else {
      // Clean simple markdown bold tags **text** for easy rendering
      const cleanedLine = line.replace(/\*\*/g, '');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9.5);
      pdf.setTextColor(51, 65, 85);
      
      const splitLines = pdf.splitTextToSize(cleanedLine, contentWidth);
      checkPageOverflow(splitLines.length * 4.5 + 3);
      
      pdf.text(splitLines, margin, y);
      y += (splitLines.length * 4.5) + 3;
    }
  }

  // Draw table if file ends with active table
  if (inTable && tableRows.length > 0) {
    drawTable(pdf, tableRows, contentWidth, margin);
  }

  // Add headers/footers dynamically on all generated pages
  const totalPages = (pdf.internal as any).getNumberOfPages();
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    pdf.setPage(pageNum);
    drawHeaderFooter(pageNum, totalPages);
  }

  function drawTable(pdfDoc: jsPDF, rows: string[][], width: number, startX: number) {
    if (rows.length === 0) return;
    
    const colCount = rows[0].length;
    const colWidth = width / colCount;
    const rowHeight = 6;
    
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.setFontSize(8);
    
    // Draw Headers
    checkPageOverflow(rowHeight + 3);
    pdfDoc.setFillColor(241, 245, 249); // slate-100
    pdfDoc.rect(startX, y, width, rowHeight, 'F');
    pdfDoc.setDrawColor(203, 213, 225); // slate-300
    pdfDoc.setLineWidth(0.1);
    pdfDoc.line(startX, y, startX + width, y);
    pdfDoc.line(startX, y + rowHeight, startX + width, y + rowHeight);
    
    // Header cells text
    rows[0].forEach((cell, idx) => {
      pdfDoc.setTextColor(30, 41, 59); // slate-800
      const cellText = pdfDoc.splitTextToSize(cell, colWidth - 4);
      pdfDoc.text(cellText, startX + (idx * colWidth) + 2, y + 4.2);
    });
    
    y += rowHeight;
    
    // Body cells
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setFontSize(7.5);
    pdfDoc.setTextColor(51, 65, 85); // slate-700
    
    for (let r = 1; r < rows.length; r++) {
      const cells = rows[r];
      let maxHeight = rowHeight;
      const parsedCells = cells.map(c => pdfDoc.splitTextToSize(c, colWidth - 4));
      
      parsedCells.forEach(pc => {
        const textH = pc.length * 4 + 2;
        if (textH > maxHeight) maxHeight = textH;
      });
      
      checkPageOverflow(maxHeight);
      
      // Draw alternating row bg
      if (r % 2 === 0) {
        pdfDoc.setFillColor(248, 250, 252); // slate-50
        pdfDoc.rect(startX, y, width, maxHeight, 'F');
      }
      
      // Draw grid line
      pdfDoc.line(startX, y + maxHeight, startX + width, y + maxHeight);
      
      parsedCells.forEach((pc, idx) => {
        pdfDoc.text(pc, startX + (idx * colWidth) + 2, y + 4);
      });
      
      y += maxHeight;
    }
    y += 5; // space after table
  }

  const slateText = 85; // proxy for slate color index

  // Save the PDF
  const safeFilename = options.solicitationId.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  pdf.save(`${safeFilename}_proposal_response.pdf`);
}
